import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

export interface BlockedPeriod {
  startDate: string;
  endDate: string;
  comment?: string;
}

export interface Equipment {
  id?: string;
  name: string;
  width: number;
  height: number;
  pixelPitch: number;
  length: number;
  deliveryHeight: number;
  deliveryWidth: number;
  isAvailable: boolean;
  blockedPeriods?: BlockedPeriod[];
}

export interface OrderLineItem {
  description: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

export interface Booking {
  id?: string;
  equipmentId?: string;
  firstName?: string;
  lastName?: string;
  customerName?: string; // Backwards compatibility
  companyName?: string;
  phone?: string;
  address: string;
  street?: string;
  postalCode?: string;
  city?: string;
  billingStreet?: string;
  billingPostalCode?: string;
  billingCity?: string;
  email: string;
  startDate: string;
  endDate: string;
  distance: number;
  durationSeconds?: number;
  durationDays: number;
  price: number;
  helperDiscount?: number;
  status: 'New' | 'Confirmed' | 'Cancelled';
  magicToken?: string;
  createdAt?: string;
  updatedAt?: string;
  notes?: string;
  sequenceNumber?: number;
  includes360Branding?: boolean;
  brandingPrice?: number;
  brandingNotes?: string;
  brandingRequiresAdvanceBooking?: boolean;
  customerProvidesHelper?: boolean;
  lineItems?: OrderLineItem[];
  sevdeskOrderId?: number;
  sevdeskContactId?: number;
  sevdeskOrderCreatedAt?: string;
}

export interface DurationTier {
  days: number;
  discount: number;
}

export interface PricingConfiguration {
  id?: string;
  minSetupPrice: number;
  pricePerKm: number;
  rentalBasePrice: number;
  setupHours: number;
  teardownHours: number;
  personCostPerHour: number;
  standardSetupPersonCount: number;
  durationTiers: DurationTier[];
  lastModified?: string;
}

export interface FAQItem {
  question: string;
  answer: string;
}

export interface FAQConfiguration {
  id?: string;
  items: FAQItem[];
  lastModified?: string;
}

export interface PriceCalculationLog {
  id?: string;
  createdAt: string;
  requestedStartDate?: string;
  requestedEndDate?: string;
  requestedLocation?: string;
  requestedCity?: string;
  distanceKm: number;
  durationDays: number;
  durationSeconds: number;
  customerProvidesHelper: boolean;
  includes360Branding: boolean;
  totalPrice: number;
  rentalCost: number;
  deliverySetupCost: number;
  brandingCost: number;
  appliedDiscount: number;
}

@Injectable({
  providedIn: 'root'
})
export class AdminService {
  private apiUrl = '/api/admin';

  constructor(private http: HttpClient) {}

  // Booking Management
  getAllBookings(): Observable<Booking[]> {
    return this.http.get<Booking[]>(`${this.apiUrl}/bookings`);
  }

  getBookingDetail(id: string): Observable<Booking> {
    return this.http.get<Booking>(`${this.apiUrl}/bookings/${id}`);
  }

  updateBookingStatus(id: string, status: string, notes?: string): Observable<Booking> {
    return this.http.put<Booking>(`${this.apiUrl}/bookings-status`, {
      id,
      status,
      notes
    });
  }

  deleteBooking(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/bookings/${id}`);
  }

  // Equipment Management
  getAllEquipment(): Observable<Equipment[]> {
    return this.http.get<Equipment[]>(`${this.apiUrl}/equipment`);
  }

  getEquipment(id: string): Observable<Equipment> {
    // Note: Backend uses {*id} route parameter which accepts slashes
    return this.http.get<Equipment>(`${this.apiUrl}/equipment/${id}`);
  }

  createEquipment(equipment: Equipment): Observable<Equipment> {
    return this.http.post<Equipment>(`${this.apiUrl}/equipment`, equipment);
  }

  updateEquipment(id: string, equipment: Equipment): Observable<Equipment> {
    // Note: Backend uses {*id} route parameter which accepts slashes
    return this.http.put<Equipment>(`${this.apiUrl}/equipment/${id}`, equipment);
  }

  deleteEquipment(id: string): Observable<void> {
    // Note: Backend uses {*id} route parameter which accepts slashes
    return this.http.delete<void>(`${this.apiUrl}/equipment/${id}`);
  }

  // Pricing Configuration
  getPricingConfiguration(): Observable<PricingConfiguration> {
    return this.http.get<PricingConfiguration>('/api/pricing/configuration');
  }

  // FAQ Configuration
  getFAQConfiguration(): Observable<FAQConfiguration> {
    return this.http.get<FAQConfiguration>('/api/pricing/faqs');
  }

  // Price Calculations
  getPriceCalculations(limit: number = 500): Observable<PriceCalculationLog[]> {
    return this.http.get<PriceCalculationLog[]>(`${this.apiUrl}/price-calculations`, {
      params: { limit: limit.toString() }
    });
  }

  deletePriceCalculation(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/price-calculations/${id}`);
  }
}
